import styled from 'styled-components';
import { FlexAlignCenterSpaceBetween } from 'components/primitives/flex';
import { useEffect, useMemo, useState } from 'react';
import { MAX_BOATS } from 'utils/sort';
import { useAtomValue } from 'jotai';
import { mediaMMdMin } from 'helpers/breakpoints';
import {
  areaParamAtom,
  areBoatsLoadingAtom,
  DSUQueryNameAtom,
  isAnyFilterActiveAtom,
  isRouterReadyAtom,
  nearParamAtom,
  otherQueryParamsAtom,
  totalBoatsCountAtom,
} from './Filter/filterStore';
import { useBoatDisplayedTrack } from './hooks/useBoatDisplayedTrack';
import { GeoBoundaries } from './GeoBoundaries';

export const composeHeadline = (headline) => (headline ? headline.replace(', USA', '') : 'this area');

const emptyText = (searchLocation: string, isAnyFilterActive: boolean) =>
  isAnyFilterActive
    ? `No boats found in ${searchLocation} that match your criteria. `
    : `No boats found in ${searchLocation}. `;

export default function BoatsListHeader({ ssrTotalCount, ssrNear, ...props }: any) {
  const near = useAtomValue(nearParamAtom);
  const area = useAtomValue(areaParamAtom);
  const jotaiTotal = useAtomValue(totalBoatsCountAtom);
  const otherParams = useAtomValue<any>(otherQueryParamsAtom);
  const DSUQueryName = useAtomValue(DSUQueryNameAtom);
  const isAnyFilterActive = useAtomValue(isAnyFilterActiveAtom);
  const areBoatsLoading = useAtomValue(areBoatsLoadingAtom);
  const routerReady = useAtomValue(isRouterReadyAtom);

  // Track whether Jotai has ever completed its first load.
  const [jotaiHasLoaded, setJotaiHasLoaded] = useState(false);
  useEffect(() => {
    if (routerReady && !areBoatsLoading) setJotaiHasLoaded(true);
  }, [routerReady, areBoatsLoading]);

  // Use SSR data until Jotai has loaded, then switch to jotaiTotal.
  const total = jotaiHasLoaded ? jotaiTotal : ssrTotalCount ?? jotaiTotal;
  const effectiveNear = near || ssrNear || '';
  // Only show "Searching" after Jotai has produced its first result (user changed a filter).
  // During SSR→Jotai handoff, keep showing the SSR count to avoid CLS.
  const showAsLoading = areBoatsLoading && jotaiHasLoaded;

  const areaName = useMemo(() => GeoBoundaries.find((boundary) => boundary.Area === area)?.AreaName, [area]);

  const headline = composeHeadline(areaName || effectiveNear || DSUQueryName);

  const [headlineStatic, setHeadlineStatic] = useState('');

  const searchHeadline = useMemo(() => {
    const localityTotal = otherParams.locality ? total : Math.min(total, MAX_BOATS);

    const totalBoatsText = showAsLoading
      ? 'Searching'
      : `${localityTotal}${total > MAX_BOATS && !otherParams.locality ? '+' : ''}`;

    if (!showAsLoading && total === 0) {
      return emptyText(headline, isAnyFilterActive);
    }
    if (effectiveNear || DSUQueryName) {
      return `${totalBoatsText} boat${localityTotal === 1 ? '' : 's'} in ${headline}`;
    }
    return `${totalBoatsText} boat${localityTotal === 1 ? '' : 's'} in this area`;
  }, [headline, DSUQueryName, total, effectiveNear, showAsLoading]);

  useEffect(() => {
    if (showAsLoading) return;

    setHeadlineStatic(searchHeadline);
  }, [searchHeadline, showAsLoading]);

  useBoatDisplayedTrack(routerReady, areBoatsLoading, headlineStatic);

  return (
    <BoatsHeader px="4px" mb="18px" gap="36px" mdMin={{ mb: '28px' }} {...props}>
      <SearchHeading data-testid="search-heading">{searchHeadline}</SearchHeading>

      {/* Removing Sort By component based on SRCH-154 */}
      {/* {!smallScreen && <SortingFilterButton />} */}
    </BoatsHeader>
  );
}

const BoatsHeader = styled(FlexAlignCenterSpaceBetween)``;

const SearchHeading = styled.h2`
  font-weight: 700;
  font-size: 18px;
  margin: 0;

  ${mediaMMdMin} {
    font-size: 20px;
  }
`;
